import { X, Plane, MapPin, Gauge, Compass, Building2 } from 'lucide-react'
import type { MonitorLiveAircraft } from '@/lib/api'

const MILITARY_PATTERNS = /^(RCH|REACH|EVAC|VIPER|EAGLE|DUKE|NAVY|HUSKY|BLUE|COBRA|SNAKE|TIGER|BONE|SPAR|ROOK|MASH|DUSTOFF|MOOSE|HERKY|CALLSIGN|PAE|OMF|PAT|HAWK|RAPTOR|STRIKE|WOLF|BEAR|LION|HOG)/i

function isMilitary(a: MonitorLiveAircraft): boolean {
  const cs = (a.callsign || '').toUpperCase()
  const country = (a.origin_country || '').toUpperCase()
  const militaryCountries = ['RU', 'CN', 'KP', 'IR', 'SY', 'US', 'GB', 'FR', 'DE', 'IL', 'IN', 'PK', 'SA']
  return MILITARY_PATTERNS.test(cs) || militaryCountries.includes(country)
}

interface Props {
  aircraft: MonitorLiveAircraft
  onClose: () => void
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-gray-600 mt-0.5">{icon}</span>
      <div className="text-[10px]">
        <span className="text-gray-600">{label}: </span>
        <span className="text-gray-400">{value}</span>
      </div>
    </div>
  )
}

export default function FlightDetailCard({ aircraft, onClose }: Props) {
  const military = isMilitary(aircraft)
  const altKm = aircraft.baro_altitude != null ? (aircraft.baro_altitude / 1000).toFixed(1) : '—'
  const speedKt = aircraft.velocity != null ? (aircraft.velocity * 1.943).toFixed(0) : '—'
  const track = aircraft.true_track != null ? `${Math.round(aircraft.true_track)}°` : '—'

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded overflow-hidden shadow-[0_0_15px_rgba(34,197,94,0.08)]">
      <div className="flex items-start justify-between px-4 py-3 border-b border-green-900/40 bg-gray-900/50">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="text-sm font-semibold text-gray-100 leading-snug font-mono">
            {aircraft.callsign || aircraft.icao24}
            {military && <span className="text-red-400 ml-1.5 text-[10px]">[MILITARY]</span>}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-gray-500">
            <Plane className="w-3.5 h-3.5 text-green-400" />
            <span>ICAO24: {aircraft.icao24}</span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-600 hover:text-gray-300 transition-colors p-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3 text-xs font-mono">
        <div className="space-y-1.5">
          {aircraft.origin_country && (
            <InfoRow icon={<Building2 className="w-3 h-3" />} label="Origin" value={aircraft.origin_country} />
          )}
          <InfoRow
            icon={<MapPin className="w-3 h-3" />}
            label="Position"
            value={`${aircraft.latitude.toFixed(4)}, ${aircraft.longitude.toFixed(4)}`}
          />
          <InfoRow icon={<Gauge className="w-3 h-3" />} label="Altitude" value={`${altKm} km`} />
          <InfoRow icon={<Gauge className="w-3 h-3" />} label="Speed" value={`${speedKt} kt`} />
          <InfoRow icon={<Compass className="w-3 h-3" />} label="Track" value={track} />
        </div>
      </div>
    </div>
  )
}
